import { FC, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Navigate } from 'react-router';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { PATHS } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { toastCaller } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';

import { useEncountersPage } from './hooks';
import { EncountersDesktopLayout } from './ui/EncountersLayout/EncountersDesktopLayout';
import { EncountersMobileLayout } from './ui/EncountersLayout/EncountersMobileLayout';

type EncountersProps = {
  className?: string;
};

export const Encounters: FC<EncountersProps> = (props) => {
  const { className } = props;
  const { formatMessage } = useIntl();

  const isEncountersEnabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_Encounters,
    ),
  );
  const currentOrganization = useAppSelector(
    organizationModel.selectors.selectCurrentOrganization,
  );

  const areFeatureFlagsReady = currentOrganization?.FeatureFlags !== undefined;
  const isAccessDenied = areFeatureFlagsReady && !isEncountersEnabled;

  useEffect(() => {
    if (!isAccessDenied) {
      return;
    }

    toastCaller({
      type: 'warning',
      heading: formatMessage({
        id: 'global.accessDenied',
        defaultMessage: 'Access denied',
      }),
      message: formatMessage({
        id: 'encounters.accessDenied.message',
        defaultMessage:
          'The Encounters page is not enabled for your organization.',
      }),
    });
  }, [isAccessDenied, formatMessage]);

  if (!areFeatureFlagsReady) {
    return null;
  }

  if (isAccessDenied) {
    return <Navigate replace to={PATHS.main} />;
  }

  return <EncountersBody className={className} />;
};

const EncountersBody: FC<EncountersProps> = (props) => {
  const { className } = props;
  const page = useEncountersPage();

  if (page.isMobile) {
    return <EncountersMobileLayout {...page} className={className} />;
  }

  return <EncountersDesktopLayout {...page} className={className} />;
};
